import React, { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CameraIcon, UserCircleIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Circular avatar used on live streams and seller surfaces.
 *
 * If the viewing user owns the avatar (editable && it's their own profile),
 * tapping the circle opens a file picker and uploads a new profile picture
 * via POST /api/me/picture. For everyone else it renders as a plain display.
 */
export default function SellerAvatar({
  name,
  picture,
  size = 96,
  editable = false,
  onUpdated,
  className = "",
  // When true, wraps the avatar in a rotating amber sparkle ring +
  // shimmer overlay + four twinkling sparkles. Used to visually mark
  // admins, founders, and any user an admin has toggled
  // `avatar_sparkle=true` for via the admin panel.
  sparkle = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localPic, setLocalPic] = useState(picture || null);
  const { setUser } = useAuth();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — max 8MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/me/picture", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLocalPic(data.picture);
      setUser && setUser(data);
      toast.success("Profile picture updated");
      if (onUpdated) onUpdated(data.picture);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const currentPic = localPic || picture;
  const dim = { width: size, height: size };

  // VIP sparkle treatment — adds the rotating amber conic-gradient
  // ring (4s loop) + a diagonal shimmer sweep + four twinkling
  // sparkle stars. Pure CSS, no JS. Wrapper grows by 6px on each
  // axis to leave room for the ring; size that absorbs back into
  // the same visual footprint thanks to `-inset-[3px]`.
  if (sparkle) {
    const wrap = size + 6;
    return (
      <div className={`relative ${className}`} data-testid="seller-avatar" style={{ width: wrap, height: wrap }}>
        <span aria-hidden className="absolute inset-0 rounded-full admin-avatar-ring" />
        <span aria-hidden className="absolute inset-[2px] rounded-full bg-white" />
        <div
          className="absolute inset-[3px] rounded-full overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-600 flex items-center justify-center text-white font-display font-black"
          style={{ ...dim, fontSize: Math.max(14, size / 3) }}
        >
          {currentPic ? (
            <img src={currentPic} alt={name || "Seller"} draggable={false} className="h-full w-full object-cover" />
          ) : (
            <span>{initials || <UserCircleIcon size={size * 0.6} />}</span>
          )}
        </div>
        <span aria-hidden className="absolute inset-[3px] rounded-full overflow-hidden pointer-events-none">
          <span className="absolute inset-0 admin-avatar-shimmer" />
        </span>
        {/* 4 sparkles, staggered delays to keep the twinkle non-uniform */}
        <Sparkle style={{ top: -2, left: "50%", marginLeft: -3, animationDelay: "0s" }} />
        <Sparkle style={{ right: -2, top: "50%", marginTop: -3, animationDelay: "0.7s" }} />
        <Sparkle style={{ bottom: -2, left: "50%", marginLeft: -3, animationDelay: "1.4s" }} />
        <Sparkle style={{ left: -2, top: "50%", marginTop: -3, animationDelay: "2.1s" }} />
        {editable && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              aria-label="Upload profile picture"
              className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white grid place-items-center ring-2 ring-white shadow-lg disabled:opacity-60 transition"
              data-testid="seller-avatar-upload-btn"
            >
              <CameraIcon size={14} weight="bold" />
            </button>
            <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="seller-avatar-file-input" />
            {uploading && (
              <div className="absolute inset-[3px] rounded-full bg-black/40 grid place-items-center text-[10px] font-bold text-white">Uploading…</div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid="seller-avatar">
      <div
        className="rounded-full overflow-hidden shadow-xl bg-gradient-to-br from-zinc-800 to-zinc-600 flex items-center justify-center text-white font-display font-black"
        style={{ ...dim, fontSize: Math.max(14, size / 3) }}
      >
        {currentPic ? (
          <img
            src={currentPic}
            alt={name || "Seller"}
            draggable={false}
            className="h-full w-full object-cover"
          />
        ) : (
          <span>{initials || <UserCircleIcon size={size * 0.6} />}</span>
        )}
      </div>
      {editable && (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            aria-label="Upload profile picture"
            className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white grid place-items-center ring-2 ring-white shadow-lg disabled:opacity-60 transition"
            data-testid="seller-avatar-upload-btn"
          >
            <CameraIcon size={14} weight="bold" />
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="seller-avatar-file-input"
          />
          {uploading && (
            <div className="absolute inset-0 rounded-full bg-black/40 grid place-items-center text-[10px] font-bold text-white">
              Uploading…
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Sparkle star — matches AdminAvatar.jsx so the visual identity is
// consistent everywhere we surface the VIP avatar treatment.
function Sparkle({ style }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 12 12"
      width={7}
      height={7}
      className="absolute admin-avatar-sparkle"
      style={style}
    >
      <path d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z" fill="#fbbf24" />
    </svg>
  );
}
